import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Browser from '@wetransfer/concorde-browser';

import { convertUploadTimeToText } from '@helper/progress';

import { TransferUploadActions } from '@app/transfer-bundle/store/actions';
import { getCurrentSize } from '@app/transfer-bundle/store/selectors';

import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import Router from '@module/router';

import Spinner from '@component/spinner';

export class Progress extends React.Component {
  constructor(props) {
    super(props);

    this.cancelTransfer = this.cancelTransfer.bind(this);
    this.toggleCancelConfirmation = this.toggleCancelConfirmation.bind(this);
  }

  componentDidMount() {
    const { abortUploadOnRefresh, transfer } = this.props;

    window.onbeforeunload = () => {
      try {
        abortUploadOnRefresh(transfer);
      } catch (e) {
        /* we don't need this catch. */
      }

      // Ask if the user is sure about leaving the page.
      return 'Are you sure you want to cancel this transfer?';
    };

    window.addEventListener('offline', this.handleOfflineListener, false);
    window.addEventListener('online', this.handleOnlineListener, false);
  }

  // Remove message about aborting transfer.
  componentWillUnmount() {
    window.onbeforeunload = null;
    window.removeEventListener('offline', this.handleOfflineListener);
    window.removeEventListener('online', this.handleOnlineListener);
  }

  handleOnlineListener = () => {
    const { resumeUploadQueue, startUploadProgress, transfer } = this.props;
    const currentSize = getCurrentSize({ transfer });

    resumeUploadQueue();
    startUploadProgress(transfer.files, currentSize, transfer.uploadStartedAt);
  };

  handleOfflineListener = () => {
    this.props.pauseUploadQueue();
    this.props.pauseUploadProgress();
  };

  cancelTransfer() {
    const {
      cancelUpload,
      resetTransfer,
      transfer,
      progressDuration,
    } = this.props;

    Analytics.trackGoogleEvent({
      category: 'Transfer',
      action: 'Cancelled',
      label: progressDuration,
      value: getCurrentSize({ transfer }),
    });

    cancelUpload(transfer);
    resetTransfer();
  }

  toggleCancelConfirmation() {
    const { transfer, pauseUpload, resumeUpload } = this.props;

    if (transfer.state === 'started') {
      return pauseUpload();
    }

    resumeUpload(transfer);
  }

  renderButtons(transfer) {
    if (transfer.state === 'paused') {
      return [
        <div key="transfer_button" className="transfer__button-container">
          <button
            onClick={this.toggleCancelConfirmation}
            className={
              'transfer__button transfer__button--cancel transfer__button--alt'
            }
          >
            {I18n.t('transfer.buttons.cancel_no')}
          </button>
          <button
            onClick={this.cancelTransfer}
            className={'transfer__button transfer__button--cancel'}
          >
            {I18n.t('transfer.buttons.cancel_yes')}
          </button>
        </div>,
      ];
    }

    const classNames = ['transfer__button transfer__button--alt'];
    let onClick = this.toggleCancelConfirmation;
    const currentSize = getCurrentSize({ transfer });

    // Don't allow cancel until the transfer has an id
    if (!transfer.id || this.props.progressBytesSentSoFar === currentSize) {
      classNames.push('transfer__button--alt-inactive');
      onClick = null;
    }

    return [
      <button
        key="transfer_button_cancel"
        onClick={onClick}
        className={classNames.join(' ')}
      >
        {I18n.t('transfer.buttons.cancel')}
      </button>,
    ];
  }

  openPanel(event) {
    event.preventDefault();
    event.stopPropagation();

    Router.navigateTo('/transfer/progress', Router.SILENT);
  }

  getMetaInfo(transfer) {
    let info = '';

    if (transfer.type === 'link') {
      info = I18n.t('transfer.progress.meta.link', {
        count: transfer.files.length,
      });
    } else if (transfer.type === 'email') {
      let filesKey = 'other_files';
      if (transfer.files.length === 1) {
        filesKey = 'one_file';
      }

      let recipientsKey = 'other_recipients';
      if (transfer.recipients.length === 1) {
        recipientsKey = 'one_recipient';
      }

      info = I18n.t(
        `transfer.progress.meta.email.${filesKey}_${recipientsKey}`,
        {
          filesCount: transfer.files.length,
          recipientsCount: transfer.recipients.length,
        }
      );
    }

    if (Browser.isMobile) {
      return info;
    }

    return (
      <a href="/transfer/progress" onClick={this.openPanel}>
        {info}
      </a>
    );
  }

  // Let's render our progress screen (spinner?) and pause buttons.
  render() {
    const {
      transfer,
      layout: {
        wallpaper: { uploadSpinnerColor, uploadSpinnerTransitionColor },
      },
      remainingUploadTime,
      progressBytesSentSoFar,
      progressPercentage,
    } = this.props;

    const buttons = (
      <div className="transfer__footer">{this.renderButtons(transfer)}</div>
    );
    const paused = transfer.state === 'paused';
    const title = paused ? 'title_cancel' : 'title';

    // Build our time remaining and bytes sent labels
    let metaInfo = <p>{this.getMetaInfo(transfer)}</p>;
    let remainingTime = <p>{convertUploadTimeToText(remainingUploadTime)}</p>;
    const currentSize = getCurrentSize({ transfer });
    let bytesSent = (
      <p>
        {I18n.t('transfer.progress.uploaded', {
          total: I18n.size(currentSize, { precision: 1 }),
          sent: I18n.size(progressBytesSentSoFar, { precision: 1 }),
        })}
      </p>
    );
    let spinnerForeground = undefined;

    // We don't show time remaining etc. during cancel confirmation screen
    if (paused) {
      remainingTime = bytesSent = metaInfo = null;
    }

    if (uploadSpinnerColor && /^#[0-9A-F]{6}$/i.test(uploadSpinnerColor)) {
      spinnerForeground = uploadSpinnerColor;
    }

    if (
      uploadSpinnerTransitionColor &&
      /^#[0-9A-F]{6}$/i.test(uploadSpinnerTransitionColor)
    ) {
      spinnerForeground = uploadSpinnerTransitionColor;
    }

    return (
      <div className="transfer__window uploader--progress">
        <div className="transfer__contents">
          <Spinner
            className="transfer__spinner"
            rotate={true}
            foreground={spinnerForeground}
            percentage={progressPercentage}
          />
          <h2>{I18n.t(`transfer.progress.${title}`)}</h2>
          {metaInfo}
          {bytesSent}
          {remainingTime}
        </div>
        {buttons}
      </div>
    );
  }
}

Progress.propTypes = {
  pauseUpload: PropTypes.func.isRequired,
  resumeUpload: PropTypes.func.isRequired,
  pauseUploadQueue: PropTypes.func.isRequired,
  resumeUploadQueue: PropTypes.func.isRequired,
  abortUploadOnRefresh: PropTypes.func.isRequired,
  cancelUpload: PropTypes.func.isRequired,
  resetTransfer: PropTypes.func.isRequired,
  transfer: PropTypes.object.isRequired,
  layout: PropTypes.object.isRequired,
  remainingUploadTime: PropTypes.number,
  startUploadProgress: PropTypes.func.isRequired,
  pauseUploadProgress: PropTypes.func.isRequired,
  progressDuration: PropTypes.number.isRequired,
  progressBytesSentSoFar: PropTypes.number.isRequired,
  progressPercentage: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => ({
  remainingUploadTime: state.transferUpload.progress.remainingTime,
  progressDuration: state.transferUpload.progress.duration,
  progressBytesSentSoFar: state.transferUpload.progress.progressBytesSentSoFar,
  progressPercentage: state.transferUpload.progress.percentage,
});

const mapDispatchToProps = {
  startUploadProgress: TransferUploadActions.startUploadProgress,
  pauseUploadProgress: TransferUploadActions.pauseUploadProgress,
  pauseUpload: TransferUploadActions.pauseUpload,
  resumeUpload: TransferUploadActions.resumeUpload,
  pauseUploadQueue: TransferUploadActions.pauseUploadQueue,
  resumeUploadQueue: TransferUploadActions.resumeUploadQueue,
  abortUploadOnRefresh: TransferUploadActions.abortUploadOnRefresh,
  cancelUpload: TransferUploadActions.cancelUpload,
  resetTransfer: TransferUploadActions.resetTransfer,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  pure: false,
})(Progress);
